/**
 * Mock Tax Services Data
 * Contains UAE Corporate Tax and VAT services
 *
 * Services include:
 * - CT Registration
 * - CT Return Filing
 * - VAT Registration
 * - VAT Return Filing
 * - Tax Residency Application
 * - Small Business Relief Election
 *
 * @mock This data will be replaced with API calls
 */

// Service statuses
export const serviceStatuses = {
  AVAILABLE: 'AVAILABLE',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  REQUIRES_DOCUMENT: 'REQUIRES_DOCUMENT'
};

// Filing periods
export const filingPeriods = {
  ANNUAL: 'ANNUAL',
  QUARTERLY: 'QUARTERLY',
  MONTHLY: 'MONTHLY',
  ONE_TIME: 'ONE_TIME'
};

/**
 * Tax Service Categories
 */
export const taxServiceCategories = [
  {
    id: 'cat-ct',
    code: 'CORPORATE_TAX',
    name: 'Corporate Tax',
    nameAr: 'ضريبة الشركات',
    description: 'UAE Corporate Tax registration and filing services',
    icon: 'business',
    color: '#1976d2',
    _isMock: true
  },
  {
    id: 'cat-vat',
    code: 'VAT',
    name: 'Value Added Tax',
    nameAr: 'ضريبة القيمة المضافة',
    description: 'VAT registration and quarterly return filing',
    icon: 'receipt_long',
    color: '#388e3c',
    _isMock: true
  },
  {
    id: 'cat-compliance',
    code: 'COMPLIANCE',
    name: 'Tax Compliance',
    nameAr: 'الامتثال الضريبي',
    description: 'Tax residency certificates and compliance documents',
    icon: 'verified',
    color: '#7b1fa2',
    _isMock: true
  }
];

/**
 * Tax Services
 */
export const mockTaxServices = [
  // Corporate Tax Services
  {
    id: 'svc-ct-reg',
    code: 'CT_REGISTRATION',
    categoryId: 'cat-ct',
    categoryCode: 'CORPORATE_TAX',
    name: 'Corporate Tax Registration',
    nameAr: 'تسجيل ضريبة الشركات',
    description: 'Register your company for UAE Corporate Tax with the Federal Tax Authority',
    longDescription: `Corporate Tax registration is mandatory for all UAE businesses. This service includes:
    - Preparation of registration application
    - Submission to Federal Tax Authority (FTA)
    - TRN (Tax Registration Number) issuance
    - Post-registration compliance guidance`,

    // Pricing
    govtCharge: 0,
    serviceCharge: 500,
    taxOnServiceCharge: 25,
    totalCharge: 525,
    currency: 'AED',

    // Timing
    estimatedDays: 5,
    filingPeriod: filingPeriods.ONE_TIME,

    // Requirements
    requiredDocuments: ['TRADE_LICENSE', 'MOA', 'ESTABLISHMENT_CARD'],
    prerequisites: [],

    // Output
    outputDocuments: ['TRN_CERTIFICATE'],

    // Status
    isActive: true,
    isPopular: true,

    _isMock: true
  },
  {
    id: 'svc-ct-return',
    code: 'CT_RETURN_FILING',
    categoryId: 'cat-ct',
    categoryCode: 'CORPORATE_TAX',
    name: 'Corporate Tax Return Filing',
    nameAr: 'تقديم إقرار ضريبة الشركات',
    description: 'Annual Corporate Tax return preparation and filing',
    longDescription: `Annual CT return filing service includes:
    - Review of financial statements
    - Calculation of taxable income
    - Preparation of CT return
    - Electronic filing with FTA
    - Filing confirmation and receipt`,

    govtCharge: 0,
    serviceCharge: 1500,
    taxOnServiceCharge: 75,
    totalCharge: 1575,
    currency: 'AED',

    estimatedDays: 10,
    filingPeriod: filingPeriods.ANNUAL,
    filingDeadline: '9 months after financial year end',

    requiredDocuments: ['TRN_CERTIFICATE', 'AUDIT_REPORT', 'TRADE_LICENSE'],
    prerequisites: ['CT_REGISTRATION'],

    outputDocuments: ['CT_RETURN'],

    isActive: true,
    isPopular: true,

    _isMock: true
  },
  {
    id: 'svc-ct-amend',
    code: 'CT_AMENDMENT',
    categoryId: 'cat-ct',
    categoryCode: 'CORPORATE_TAX',
    name: 'CT Return Amendment',
    nameAr: 'تعديل إقرار ضريبة الشركات',
    description: 'Amend a previously filed Corporate Tax return',
    longDescription: `If you need to correct or update a previously filed CT return, this service covers:
    - Review of original filing
    - Identification of required amendments
    - Preparation of amended return
    - Submission to FTA
    - Handling of any resulting tax adjustments`,

    govtCharge: 0,
    serviceCharge: 1000,
    taxOnServiceCharge: 50,
    totalCharge: 1050,
    currency: 'AED',

    estimatedDays: 7,
    filingPeriod: filingPeriods.ONE_TIME,

    requiredDocuments: ['TRN_CERTIFICATE', 'CT_RETURN'],
    prerequisites: ['CT_RETURN_FILING'],

    outputDocuments: ['CT_RETURN'],

    isActive: true,
    isPopular: false,

    _isMock: true
  },

  // VAT Services
  {
    id: 'svc-vat-reg',
    code: 'VAT_REGISTRATION',
    categoryId: 'cat-vat',
    categoryCode: 'VAT',
    name: 'VAT Registration',
    nameAr: 'التسجيل في ضريبة القيمة المضافة',
    description: 'Register for VAT with the Federal Tax Authority',
    longDescription: `VAT registration is mandatory for businesses with taxable supplies exceeding AED 375,000. Service includes:
    - Assessment of registration requirement
    - Preparation of VAT registration application
    - Submission to FTA
    - VAT TRN issuance
    - Initial compliance setup`,

    govtCharge: 0,
    serviceCharge: 500,
    taxOnServiceCharge: 25,
    totalCharge: 525,
    currency: 'AED',

    estimatedDays: 5,
    filingPeriod: filingPeriods.ONE_TIME,

    requiredDocuments: ['TRADE_LICENSE', 'ESTABLISHMENT_CARD'],
    prerequisites: [],

    outputDocuments: ['VAT_CERTIFICATE'],

    isActive: true,
    isPopular: true,

    _isMock: true
  },
  {
    id: 'svc-vat-return',
    code: 'VAT_RETURN_FILING',
    categoryId: 'cat-vat',
    categoryCode: 'VAT',
    name: 'VAT Return Filing',
    nameAr: 'تقديم إقرار ضريبة القيمة المضافة',
    description: 'Quarterly VAT return preparation and filing',
    longDescription: `Quarterly VAT return filing service includes:
    - Review of sales and purchase records
    - Calculation of output and input VAT
    - Preparation of VAT return
    - Electronic filing with FTA
    - Payment/refund processing guidance`,

    govtCharge: 0,
    serviceCharge: 800,
    taxOnServiceCharge: 40,
    totalCharge: 840,
    currency: 'AED',

    estimatedDays: 5,
    filingPeriod: filingPeriods.QUARTERLY,
    filingDeadline: '28th of month following quarter end',

    requiredDocuments: ['VAT_CERTIFICATE'],
    prerequisites: ['VAT_REGISTRATION'],

    outputDocuments: ['VAT_RETURN'],

    isActive: true,
    isPopular: true,

    _isMock: true
  },

  // Compliance Services
  {
    id: 'svc-tax-residency',
    code: 'TAX_RESIDENCY_APP',
    categoryId: 'cat-compliance',
    categoryCode: 'COMPLIANCE',
    name: 'Tax Residency Certificate',
    nameAr: 'شهادة الإقامة الضريبية',
    description: 'Apply for UAE Tax Residency Certificate',
    longDescription: `Tax Residency Certificate (TRC) is required to claim tax treaty benefits. Service includes:
    - Eligibility assessment
    - Application preparation
    - Supporting document compilation
    - Submission to Ministry of Finance
    - Certificate issuance (valid for 1 year)`,

    govtCharge: 2000,
    serviceCharge: 500,
    taxOnServiceCharge: 25,
    totalCharge: 2525,
    currency: 'AED',

    estimatedDays: 15,
    filingPeriod: filingPeriods.ANNUAL,

    requiredDocuments: ['TRADE_LICENSE', 'TRN_CERTIFICATE', 'AUDIT_REPORT'],
    prerequisites: ['CT_REGISTRATION'],

    outputDocuments: ['TAX_RESIDENCY_CERT'],

    isActive: true,
    isPopular: false,

    _isMock: true
  },
  {
    id: 'svc-sbr-election',
    code: 'SMALL_BUSINESS_ELECT',
    categoryId: 'cat-compliance',
    categoryCode: 'COMPLIANCE',
    name: 'Small Business Relief Election',
    nameAr: 'اختيار إعفاء الأعمال الصغيرة',
    description: 'Elect for Small Business Relief (revenue ≤ AED 3M)',
    longDescription: `Small Business Relief allows qualifying businesses to be treated as having no taxable income. Eligibility:
    - Revenue not exceeding AED 3 million
    - Valid for tax periods starting before 1 January 2027
    - Election must be made for each tax period

    Service includes eligibility verification and election filing.`,

    govtCharge: 0,
    serviceCharge: 300,
    taxOnServiceCharge: 15,
    totalCharge: 315,
    currency: 'AED',

    estimatedDays: 3,
    filingPeriod: filingPeriods.ANNUAL,

    requiredDocuments: ['TRN_CERTIFICATE', 'AUDIT_REPORT'],
    prerequisites: ['CT_REGISTRATION'],

    outputDocuments: ['SMALL_BUSINESS_ELECTION'],

    isActive: true,
    isPopular: false,

    _isMock: true
  },
  {
    id: 'svc-tax-agent',
    code: 'TAX_AGENT_APPOINT',
    categoryId: 'cat-compliance',
    categoryCode: 'COMPLIANCE',
    name: 'Tax Agent Appointment',
    nameAr: 'تعيين وكيل ضريبي',
    description: 'Appoint Thaklis as your authorized tax agent',
    longDescription: `Appointing a licensed tax agent allows for:
    - Representation before the FTA
    - Filing of tax returns on your behalf
    - Handling of tax audits and inquiries
    - Access to your EmaraTax account

    Thaklis is a licensed tax agent (TAN: TA-XXXX).`,

    govtCharge: 0,
    serviceCharge: 200,
    taxOnServiceCharge: 10,
    totalCharge: 210,
    currency: 'AED',

    estimatedDays: 2,
    filingPeriod: filingPeriods.ANNUAL,

    requiredDocuments: ['TRN_CERTIFICATE'],
    prerequisites: ['CT_REGISTRATION'],

    outputDocuments: ['TAX_AGENT_LETTER'],

    isActive: true,
    isPopular: false,

    _isMock: true
  }
];

/**
 * Organization Tax Profile
 * Extracted from uploaded tax documents
 */
export const mockTaxProfile = {
  'org-001': {
    organizationId: 'org-001',
    organizationName: 'ABC Trading LLC',

    // CT Status
    ctRegistered: true,
    trn: '100123456789012',
    ctRegistrationDate: '2024-06-01',

    // VAT Status
    vatRegistered: true,
    vatTrn: '100123456789012',
    vatRegistrationDate: '2018-01-01',
    vatCategory: 'Mandatory',

    // Financial Info (from Audit)
    lastAuditedRevenue: 5250000,
    lastAuditedProfit: 725000,
    lastAuditYear: '2024',
    financialYearEnd: '12-31',

    // Compliance
    eligibleForSmallBusinessRelief: false,
    smallBusinessElectionActive: false,
    taxAgentAppointed: false,

    // Filing Status
    filingStatus: {
      ct: {
        lastFiledPeriod: '2023',
        nextDueDate: '2025-09-30',
        status: 'ON_TIME'
      },
      vat: {
        lastFiledPeriod: 'Q3 2024',
        nextDueDate: '2025-01-28',
        status: 'DUE_SOON'
      }
    },

    // Calculated Tax
    estimatedCtLiability: {
      taxableIncome: 850000,
      taxRate: 0.09,
      estimatedTax: 42750,
      taxPeriod: '2024'
    },

    lastUpdated: '2025-01-30T10:00:00Z',
    _isMock: true
  }
};

/**
 * Tax Service Requests (History)
 */
export const mockTaxServiceRequests = [
  {
    id: 'req-001',
    serviceId: 'svc-ct-reg',
    serviceCode: 'CT_REGISTRATION',
    serviceName: 'Corporate Tax Registration',
    organizationId: 'org-001',

    status: 'COMPLETED',
    requestedAt: '2024-05-25T10:00:00Z',
    completedAt: '2024-06-01T14:30:00Z',

    totalCharge: 525,
    paymentStatus: 'PAID',

    outputDocumentId: 'doc-004',
    notes: 'TRN issued successfully',

    _isMock: true
  },
  {
    id: 'req-002',
    serviceId: 'svc-vat-return',
    serviceCode: 'VAT_RETURN_FILING',
    serviceName: 'VAT Return Filing',
    organizationId: 'org-001',

    status: 'IN_PROGRESS',
    requestedAt: '2025-01-20T09:00:00Z',
    completedAt: null,

    totalCharge: 840,
    paymentStatus: 'PAID',

    outputDocumentId: null,
    notes: 'Q4 2024 VAT return - processing',
    taxPeriod: 'Q4 2024',

    _isMock: true
  }
];

/**
 * Upcoming Tax Deadlines
 */
export const mockTaxDeadlines = [
  {
    id: 'dl-001',
    type: 'VAT_RETURN',
    description: 'Q4 2024 VAT Return',
    dueDate: '2025-01-28',
    daysUntilDue: -2, // Overdue
    status: 'OVERDUE',
    relatedServiceCode: 'VAT_RETURN_FILING',
    _isMock: true
  },
  {
    id: 'dl-002',
    type: 'CT_RETURN',
    description: '2024 Corporate Tax Return',
    dueDate: '2025-09-30',
    daysUntilDue: 243,
    status: 'UPCOMING',
    relatedServiceCode: 'CT_RETURN_FILING',
    _isMock: true
  },
  {
    id: 'dl-003',
    type: 'VAT_RETURN',
    description: 'Q1 2025 VAT Return',
    dueDate: '2025-04-28',
    daysUntilDue: 88,
    status: 'UPCOMING',
    relatedServiceCode: 'VAT_RETURN_FILING',
    _isMock: true
  }
];

/**
 * Helper Functions
 */

export const getTaxServicesByCategory = (categoryCode) => {
  return mockTaxServices.filter(s => s.categoryCode === categoryCode && s.isActive);
};

export const getTaxServiceById = (serviceId) => {
  return mockTaxServices.find(s => s.id === serviceId);
};

export const getTaxServiceByCode = (code) => {
  return mockTaxServices.find(s => s.code === code);
};

export const getTaxProfile = (orgId) => {
  return mockTaxProfile[orgId] || null;
};

export const getTaxServiceRequests = (orgId) => {
  return mockTaxServiceRequests.filter(r => r.organizationId === orgId);
};

export const getUpcomingDeadlines = (orgId) => {
  // In real app, would filter by org
  return mockTaxDeadlines.sort((a, b) => a.daysUntilDue - b.daysUntilDue);
};

export const getOverdueDeadlines = (orgId) => {
  return mockTaxDeadlines.filter(d => d.status === 'OVERDUE');
};

export const checkServiceEligibility = (serviceCode, orgId) => {
  const service = getTaxServiceByCode(serviceCode);
  const profile = getTaxProfile(orgId);

  if (!service || !profile) {
    return { eligible: false, reason: 'Service or profile not found' };
  }

  // Check prerequisites
  for (const prereq of service.prerequisites) {
    const prereqService = getTaxServiceByCode(prereq);
    const completed = mockTaxServiceRequests.find(
      r => r.organizationId === orgId &&
           r.serviceCode === prereq &&
           r.status === 'COMPLETED'
    );
    if (!completed) {
      return {
        eligible: false,
        reason: `Requires ${prereqService?.name || prereq} to be completed first`
      };
    }
  }

  // Check Small Business Relief eligibility
  if (serviceCode === 'SMALL_BUSINESS_ELECT' && profile.lastAuditedRevenue > 3000000) {
    return {
      eligible: false,
      reason: 'Revenue exceeds AED 3 million threshold'
    };
  }

  return { eligible: true, reason: null };
};

export default mockTaxServices;
